import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.response.use(
  res => res,
  err => {
    const detail = err.response?.data?.detail;
    let message = 'Terjadi kesalahan';
    if (typeof detail === 'string') message = detail;
    else if (Array.isArray(detail)) message = detail.map(e => e?.msg || String(e)).join(', ');
    return Promise.reject({ ...err, message });
  }
);

export default api;

export const authApi = {
  login: d => api.post('/auth/login', d),
  register: d => api.post('/auth/register', d),
  logout: () => api.post('/auth/logout'),
  loginDemo: role => api.post('/auth/demo-login', { role }),
  me: () => api.get('/auth/me'),
};

export const storesApi = {
  getCurrent: () => api.get('/stores/current'),
  update: d => api.put('/stores/current', d),
};

export const usersApi = {
  list: () => api.get('/users'),
  create: d => api.post('/users', d),
  update: (id, d) => api.put(`/users/${id}`, d),
  remove: id => api.delete(`/users/${id}`),
};

export const customersApi = {
  list: p => api.get('/customers', { params: p }),
  create: d => api.post('/customers', d),
  update: (id, d) => api.put(`/customers/${id}`, d),
  remove: id => api.delete(`/customers/${id}`),
  get: id => api.get(`/customers/${id}`),
  getStats: (id, params) => api.get(`/customers/${id}/stats`, { params }),
  sendWa: (id, message) => api.post(`/customers/${id}/send-wa`, { message }),
  getSegmentSummary: () => api.get('/customers/segments/summary'),
  listSegment: params => api.get('/customers/segments', { params }),
  broadcastSegment: (segment, message) => api.post(`/customers/segments/${segment}/broadcast`, { message }),
  listCustomSegments: () => api.get('/customers/custom-segments'),
  createCustomSegment: data => api.post('/customers/custom-segments', data),
  updateCustomSegment: (id, data) => api.put(`/customers/custom-segments/${id}`, data),
  removeCustomSegment: id => api.delete(`/customers/custom-segments/${id}`),
  customSegmentMembers: (id, params) => api.get(`/customers/custom-segments/${id}/members`, { params }),
  broadcastCustomSegment: (id, message) => api.post(`/customers/custom-segments/${id}/broadcast`, { message }),
  lookupByPhone: phone => api.get('/customers/lookup', { params: { phone } }),
};

export const categoriesApi = {
  list: () => api.get('/categories'),
  create: d => api.post('/categories', d),
  update: (id, d) => api.put(`/categories/${id}`, d),
  remove: id => api.delete(`/categories/${id}`),
};

export const productsApi = {
  list: params => api.get('/products', { params }),
  create: d => api.post('/products', d),
  update: (id, d) => api.put(`/products/${id}`, d),
  remove: id => api.delete(`/products/${id}`),
};

export const ordersApi = {
  list: p => api.get('/orders', { params: p }),
  create: d => api.post('/orders', d),
  get: id => api.get(`/orders/${id}`),
  updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
  updateDiagnosis: (id, d) => api.put(`/orders/${id}/diagnosis`, d),
  getPayments: id => api.get(`/orders/${id}/payments`),
  addPayment: (id, d) => api.post(`/orders/${id}/payments`, d),
  getReceiptUrl: (id, format) => `${BASE}/orders/${id}/receipt?format=${format}`,
  getWaLogs: id => api.get(`/orders/${id}/wa-logs`),
};

export const cashDrawerApi = {
  getCurrent: () => api.get('/cash-drawer/current'),
  open: d => api.post('/cash-drawer/open', d),
  close: d => api.post('/cash-drawer/close', d),
  history: () => api.get('/cash-drawer/history'),
};

export const dashboardApi = {
  getSummary: () => api.get('/dashboard/summary'),
  getSalesChart: () => api.get('/dashboard/sales-chart'),
  getOnboarding: () => api.get('/dashboard/onboarding'),
  completeOnboarding: () => api.post('/dashboard/onboarding/complete'),
};

export const publicApi = {
  track: code => axios.get(`${BASE}/track/${code}`),
};

export const reportsApi = {
  getSummary: (start, end) => api.get('/reports/summary', { params: { start, end } }),
  getByStaff: (start, end) => api.get('/reports/by-staff', { params: { start, end } }),
  getReceivables: () => api.get('/reports/receivables'),
  getProductPerformance: (start, end) => api.get('/reports/product-performance', { params: { start, end } }),
  getExportUrl: (format, start, end) => `${BASE}/reports/export?format=${format}&start=${start}&end=${end}`,
};

export const waLogsApi = {
  list: (p) => api.get('/wa/logs', { params: p }),
  getStats: (p) => api.get('/wa/logs/stats', { params: p }),
};

export const calendarApi = {
  getEvents: (start, end) => api.get('/calendar/events', { params: { start, end } }),
};

export const waApi = {
  sendOrder: (orderId, messageType, customMessage = null) =>
    api.post('/wa/send-order', { order_id: orderId, message_type: messageType, custom_message: customMessage }),
  sendCustom: (target, message) => api.post('/wa/send-custom', { target, message }),
  testToken: (customToken = null, target = null) => api.post('/wa/test', { custom_token: customToken, target }),
  getDefaultTemplates: () => api.get('/wa/templates/defaults'),
  sendDailyReport: () => api.post('/wa/send-daily-report'),
  getStatus: () => api.get('/wa/status'),
};

export const operationalCostsApi = {
  list: (start, end) => api.get('/operational-costs', { params: { start, end } }),
  listPaginated: params => api.get('/operational-costs', { params }),
  create: d => api.post('/operational-costs', d),
  update: (id, d) => api.put(`/operational-costs/${id}`, d),
  remove: id => api.delete(`/operational-costs/${id}`),
};

export const inventoryApi = {
  summary: () => api.get('/inventory/summary'),
  items: params => api.get('/inventory/items', { params }),
  movements: params => api.get('/inventory/movements', { params }),
  restock: (productId, data) => api.post(`/inventory/products/${productId}/restock`, data),
  adjust: (productId, data) => api.post(`/inventory/products/${productId}/adjustment`, data),
};

export const uploadsApi = {
  uploadPhoto: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/uploads/photo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getPhotoUrl: (path) => `${BASE}/uploads/photo/${path}`,
  uploadQris: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/uploads/qris', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getQrisUrl: (path) => `${BASE}/uploads/qris/${path}`,
};

export const paymentProofsApi = {
  // Public — no auth (use axios directly)
  submit: (orderId, amount, notes, file) => {
    const form = new FormData();
    form.append('amount', amount);
    form.append('notes', notes || '');
    if (file) form.append('file', file);
    return axios.post(`${BASE}/orders/${orderId}/payment-proof`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Admin
  list: (orderId) => api.get(`/orders/${orderId}/payment-proofs`),
  confirm: (orderId, proofId, confirmedAmount) =>
    api.put(`/orders/${orderId}/payment-proofs/${proofId}/confirm`, { confirmed_amount: confirmedAmount }),
  reject: (orderId, proofId) =>
    api.put(`/orders/${orderId}/payment-proofs/${proofId}/reject`),
};
